/** Admin — write and publish CCS Insights articles. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ExternalLink, Star } from "lucide-react";
import { formatSpotlightDate } from "@/components/spotlight/spotlightUtils";
import ImageUploadField from "@/components/ImageUploadField";
import SignatureSetting from "@/components/SignatureSetting";
import RichTextEditor from "@/components/RichTextEditor";

const EMPTY = { title: "", category: "Communication", summary: "", content: "", image_url: "", published: false };

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">{label}</div>
    {children}
  </div>
);

const InsightFormDialog = ({ open, onOpenChange, editing, categories, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [open, editing]);

  const save = async () => {
    if (!form.title || !form.summary || !form.content) {
      toast.error("Please complete the title, summary and article content.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/insights/${editing.id}`, form);
        toast.success("Insight updated.");
      } else {
        await api.post("/insights", form);
        toast.success("Insight created.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF5] border-[#EBE3D2]" data-testid="insight-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2A1810]">{editing?.id ? "Edit Insight" : "New Insight"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Title *">
            <Input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="insight-form-title" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setF("category", v)}>
                <SelectTrigger data-testid="insight-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setF("image_url", url)}
            label="Feature image"
            testid="insight-form-image"
          />
          <Field label="One-sentence summary *">
            <Textarea rows={2} value={form.summary} onChange={(e) => setF("summary", e.target.value)} data-testid="insight-form-summary" />
          </Field>
          <Field label="Article content *">
            <RichTextEditor value={form.content} onChange={(v) => setF("content", v)} minHeight={260} testid="insight-form-content" />
          </Field>
          <div className="flex items-center justify-between bg-white border border-[#EBE3D2] rounded-xl px-4 py-3">
            <div className="text-sm font-medium text-[#2A1810]">Published (visible to the public)</div>
            <Switch checked={form.published} onCheckedChange={(v) => setF("published", v)} data-testid="insight-form-published" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="insight-form-save">
            {saving ? "Saving…" : editing?.id ? "Save changes" : "Create Insight"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function InsightsAdminPage() {
  const [insights, setInsights] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/insights").then((r) => {
      setInsights(r.data.insights || []);
      setCategories(r.data.categories || []);
    }).catch(() => {});
  };
  useEffect(load, []);

  const togglePublish = async (a) => {
    try {
      await api.put(`/insights/${a.id}`, { published: !a.published });
      toast.success(a.published ? "Moved to draft." : "Published.");
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/insights/${a.id}`);
      toast.success("Insight deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="space-y-6" data-testid="insights-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[#2A1810]">CCS Insights</h1>
          <p className="text-sm text-[#6B5944] mt-1">
            Write and publish thought pieces · <a href="/insights" target="_blank" rel="noopener noreferrer" className="text-[#C28A2E] hover:underline">view public page</a>
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="new-insight-button">
          <Plus size={16} className="mr-1.5" /> New Insight
        </Button>
      </div>

      <div className="space-y-3">
        {insights.length === 0 && (
          <div className="py-16 text-center text-[#6B5944] border border-dashed border-[#EBE3D2] rounded-2xl">
            No insights yet — write your first thought piece.
          </div>
        )}
        {insights.map((a) => (
          <div key={a.id} className="bg-white border border-[#EBE3D2] rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-insight-row-${a.id}`}>
            {a.image_url && <img src={a.image_url} alt="" className="w-16 h-12 object-cover rounded-md hidden sm:block" />}
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#2A1810]">{a.title}</span>
                {a.published ? (
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Published</Badge>
                ) : (
                  <Badge variant="outline" className="text-[#6B5944]">Draft</Badge>
                )}
              </div>
              <div className="text-xs text-[#6B5944] mt-1">
                {a.category} · {a.reading_time} min read{a.published_at ? ` · ${formatSpotlightDate(a.published_at)}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {a.published && (
                <a href={`/insights/${a.id}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#6B5944] hover:text-[#C28A2E]" title="View" data-testid={`view-insight-${a.id}`}>
                  <ExternalLink size={15} />
                </a>
              )}
              <Button variant="outline" size="sm" onClick={() => togglePublish(a)} data-testid={`toggle-insight-publish-${a.id}`}>
                <Star size={14} className="mr-1" /> {a.published ? "Unpublish" : "Publish"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setEditing(a); setFormOpen(true); }} data-testid={`edit-insight-${a.id}`}>
                <Pencil size={14} />
              </Button>
              <Button variant="outline" size="sm" onClick={() => remove(a)} className="text-red-600 hover:text-red-700" data-testid={`delete-insight-${a.id}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <InsightFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} categories={categories} onSaved={load} />

      <SignatureSetting />
    </div>
  );
}
